import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { PsoFormulaParameter, PsoFormulaParameterBody } from './types';

export const psoFormulaParametersApi = createApi({
  reducerPath: 'psoFormulaParametersApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/pso-formula-parameters`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['PsoFormulaParameter', 'PsoFormulaParameters'],
  endpoints: builder => ({
    getPsoFormulaParametersI: builder.infiniteQuery<
      PsoFormulaParameter[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PsoFormulaParameter', 'PsoFormulaParameters'],
    }),
    getPsoFormulaParameterById: builder.query<PsoFormulaParameter, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [
        { type: 'PsoFormulaParameter', id },
      ],
    }),
    createPsoFormulaParameter: builder.mutation<
      string,
      PsoFormulaParameterBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['PsoFormulaParameters'],
    }),
    updatePsoFormulaParameter: builder.mutation<
      void,
      { id: string; body: PsoFormulaParameterBody }
    >({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'PsoFormulaParameters',
        { type: 'PsoFormulaParameter', id },
      ],
    }),
  }),
});

export const {
  useGetPsoFormulaParameterByIdQuery,
  useCreatePsoFormulaParameterMutation,
  useUpdatePsoFormulaParameterMutation,
} = psoFormulaParametersApi;

export const useGetPsoFormulaParametersInfiniteQuery =
  normalizeInfiniteQueryData<PsoFormulaParameter>(
    psoFormulaParametersApi.useGetPsoFormulaParametersIInfiniteQuery,
  );
